import {
  generateAuthenticationOptions,
  generateRegistrationOptions,
  verifyAuthenticationResponse,
  verifyRegistrationResponse,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
  type WebAuthnCredential,
} from "@simplewebauthn/server";
import type { Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import {
  RuntimePasskeyStore,
  type StoredPasskeyCredential,
} from "./credential-store";
import type { AuthRuntimeDatabase } from "./runtime-db";

const DEFAULT_USER_NAME = "Anchor";
const DEFAULT_RP_NAME = "Brain";

const clientDataSchema = z.looseObject({
  challenge: z.string().optional(),
});

export interface PasskeyServiceOptions {
  runtimeDatabase: AuthRuntimeDatabase;
  rpName?: string;
  logger?: Logger;
}

export interface WebAuthnRequestContext {
  origin: string;
  rpID: string;
}

export interface PasskeyRegistrationUser {
  subject: string;
  userName?: string;
  userDisplayName?: string;
}

export type RegistrationVerifyResult =
  { verified: true; subject: string } | { verified: false };

export type AuthenticationVerifyResult =
  { verified: true; subject: string } | { verified: false };

export class PasskeyService {
  private readonly store: RuntimePasskeyStore;
  private readonly rpName: string;
  private readonly logger: Logger | undefined;

  constructor(options: PasskeyServiceOptions) {
    this.store = new RuntimePasskeyStore(options.runtimeDatabase);
    this.rpName = options.rpName ?? DEFAULT_RP_NAME;
    this.logger = options.logger;
  }

  async hasCredentials(): Promise<boolean> {
    return this.store.hasCredentials();
  }

  async generateRegistrationOptions(
    context: WebAuthnRequestContext,
    user: PasskeyRegistrationUser,
  ): Promise<PublicKeyCredentialCreationOptionsJSON> {
    const existingCredentials = await this.store.listCredentials(user.subject);
    const userName = user.userName ?? DEFAULT_USER_NAME;
    const userDisplayName = user.userDisplayName ?? userName;
    const options = await generateRegistrationOptions({
      rpName: this.rpName,
      rpID: context.rpID,
      userName,
      userDisplayName,
      attestationType: "none",
      authenticatorSelection: {
        residentKey: "required",
        userVerification: "required",
      },
      excludeCredentials: existingCredentials.map((credential) => ({
        id: credential.id,
        ...(credential.transports ? { transports: credential.transports } : {}),
      })),
    });

    await this.store.saveRegistrationChallenge(options.challenge, user.subject);
    return options;
  }

  async verifyRegistrationResponse(
    response: RegistrationResponseJSON,
    context: WebAuthnRequestContext,
    expectedSubject?: string,
  ): Promise<RegistrationVerifyResult> {
    const challenge = response.response.clientDataJSON
      ? getChallengeFromClientData(response.response.clientDataJSON)
      : undefined;
    if (!challenge) {
      return { verified: false };
    }

    const storedChallenge =
      await this.store.consumeRegistrationChallenge(challenge);
    if (
      !storedChallenge ||
      (expectedSubject !== undefined &&
        storedChallenge.subject !== expectedSubject)
    ) {
      return { verified: false };
    }

    const result = await verifyRegistrationResponse({
      response,
      expectedChallenge: challenge,
      expectedOrigin: context.origin,
      expectedRPID: context.rpID,
      requireUserVerification: true,
    });

    if (!result.verified) {
      return { verified: false };
    }

    const now = Math.floor(Date.now() / 1000);
    const credential = result.registrationInfo.credential;
    await this.store.addCredential({
      id: credential.id,
      public_key: bytesToBase64Url(credential.publicKey),
      counter: credential.counter,
      ...(credential.transports ? { transports: credential.transports } : {}),
      subject: storedChallenge.subject,
      user_name: DEFAULT_USER_NAME,
      credential_device_type: result.registrationInfo.credentialDeviceType,
      credential_backed_up: result.registrationInfo.credentialBackedUp,
      created_at: now,
      updated_at: now,
    });

    this.logger?.info("Registered passkey credential", {
      credentialId: credential.id,
    });
    return { verified: true, subject: storedChallenge.subject };
  }

  async listCredentials(): Promise<StoredPasskeyCredential[]> {
    return this.store.listCredentials();
  }

  async generateAuthenticationOptions(
    context: WebAuthnRequestContext,
  ): Promise<PublicKeyCredentialRequestOptionsJSON> {
    const options = await generateAuthenticationOptions({
      rpID: context.rpID,
      userVerification: "required",
    });

    await this.store.saveAuthenticationChallenge(options.challenge);
    return options;
  }

  async verifyAuthenticationResponse(
    response: AuthenticationResponseJSON,
    context: WebAuthnRequestContext,
  ): Promise<AuthenticationVerifyResult> {
    const challenge = response.response.clientDataJSON
      ? getChallengeFromClientData(response.response.clientDataJSON)
      : undefined;
    if (!challenge) {
      return { verified: false };
    }

    const storedChallenge =
      await this.store.consumeAuthenticationChallenge(challenge);
    if (!storedChallenge) {
      return { verified: false };
    }

    const credential = await this.store.getCredential(response.id);
    if (!credential) {
      return { verified: false };
    }

    const result = await verifyAuthenticationResponse({
      response,
      expectedChallenge: challenge,
      expectedOrigin: context.origin,
      expectedRPID: context.rpID,
      credential: toWebAuthnCredential(credential),
      requireUserVerification: true,
    });

    if (!result.verified) {
      return { verified: false };
    }

    await this.store.updateCredentialCounter(
      result.authenticationInfo.credentialID,
      result.authenticationInfo.newCounter,
    );
    return { verified: true, subject: credential.subject };
  }
}

function bytesToBase64Url(bytes: Uint8Array): string {
  return Buffer.from(bytes).toString("base64url");
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const buffer = Buffer.from(value, "base64url");
  const arrayBuffer = buffer.buffer.slice(
    buffer.byteOffset,
    buffer.byteOffset + buffer.byteLength,
  );
  return new Uint8Array(arrayBuffer);
}

function toWebAuthnCredential(
  credential: StoredPasskeyCredential,
): WebAuthnCredential {
  return {
    id: credential.id,
    publicKey: base64UrlToBytes(credential.public_key),
    counter: credential.counter,
    ...(credential.transports ? { transports: credential.transports } : {}),
  };
}

function getChallengeFromClientData(
  clientDataJSON: string,
): string | undefined {
  try {
    return clientDataSchema.parse(
      JSON.parse(Buffer.from(clientDataJSON, "base64url").toString("utf8")),
    ).challenge;
  } catch {
    return undefined;
  }
}
